import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { colors, customShadows, borderRadiusPx } from "../../../../theme";
import CommonButton from "../inputs/CommonButton";

/**
 * AccessDeniedScreen - Full-page or card component for unauthorized / restricted access states.
 */
const AccessDeniedScreen = ({
  title = "Admin Access Required",
  message,
  walletAddress = "",
  roleName = "administrator",
  actionText = "Return to Homepage",
  actionPath = "/",
  onAction,
  icon,
  maxWidth = 480,
  minHeight = "100vh",
  sx = {},
  cardSx = {},
}) => {
  const history = useHistory();

  const formattedAddress = walletAddress
    ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
    : "None";

  const defaultMessage =
    title === "Account Access Denied" || title === "Account Not Approved"
      ? "You must be a registered Creator or organization to view this page."
      : `Your connected wallet (${formattedAddress}) does not have ${roleName} privileges for the AutoBET smart contracts.`;

  const handleAction = () => {
    if (onAction && typeof onAction === "function") {
      onAction();
    } else if (actionPath) {
      history.push(actionPath);
    }
  };

  return (
    <Box
      sx={{
        minHeight: minHeight,
        backgroundColor: colors.dashboard.canvas,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        ...sx,
      }}
    >
      <Box
        sx={{
          p: 5,
          maxWidth: maxWidth,
          width: "100%",
          textAlign: "center",
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.background.paper,
          boxShadow: customShadows.dialog,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          ...cardSx,
        }}
      >
        {icon && <Box sx={{ mb: 2 }}>{icon}</Box>}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: colors.slate[900], mb: 1.5 }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.slate[500], mb: 3, lineHeight: 1.6 }}
        >
          {message || defaultMessage}
        </Typography>
        <CommonButton
          variant="contained"
          onClick={handleAction}
        >
          {actionText}
        </CommonButton>
      </Box>
    </Box>
  );
};

AccessDeniedScreen.propTypes = {
  title: PropTypes.string,
  message: PropTypes.node,
  walletAddress: PropTypes.string,
  roleName: PropTypes.string,
  actionText: PropTypes.string,
  actionPath: PropTypes.string,
  onAction: PropTypes.func,
  icon: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
  cardSx: PropTypes.object,
};

export default AccessDeniedScreen;
